import { computed, type ComputedRef } from "vue"
import { useRoute } from "vue-router"
import { BenefitTypeEnum } from "@vitable/asclepius-api-client-ts"
import { useEnrollmentStore } from "@/modules/enrollment-new/stores/enrollment-new-store"
import { useUserStore } from "@/store/user-store"
import { isMedicareEligible } from "@/modules/enrollment-new/enrollment-flow-helpers"
import {
  buildEnrollmentSegments,
  collapseToBar,
  findCurrentSegmentIndex,
  type BarEntry,
  type EnrollmentSegment
} from "@/modules/enrollment-new/enrollment-segments"
import { VPC_STEP } from "@common/utilities/benefit-utils"
import type { EnrollmentBenefitStep, NonVpcBenefitElection } from "@/modules/enrollment-new/enrollment-flow-types"

export interface UseStepperSegments {
  segments: ComputedRef<EnrollmentSegment[]>
  bar: ComputedRef<BarEntry[]>
  currentIndex: ComputedRef<number>
  currentBarIndex: ComputedRef<number>
}

export function useStepperSegments(): UseStepperSegments {
  const route = useRoute()
  const enrollmentStore = useEnrollmentStore()
  const userStore = useUserStore()

  const segments = computed<EnrollmentSegment[]>(() =>
    buildEnrollmentSegments({
      availableBenefitTypes: enrollmentStore.activeFlowAvailableBenefitTypes,
      electionsByType: collectElectionsByType(enrollmentStore.activeFlowAvailableBenefitTypes, (type) =>
        enrollmentStore.activeFlowElectionsByBenefitTypeAndYear(type).flatMap((g) => g.elections)
      ),
      vpcElection: enrollmentStore.activeFlowElectionsPackage?.vpcElection,
      query: route.query,
      medicareEligibleIn: isMedicareEligible(userStore.member)
    })
  )

  const bar = computed<BarEntry[]>(() => collapseToBar(segments.value))

  const currentIndex = computed<number>(() => findCurrentSegmentIndex(segments.value, route))

  const currentBarIndex = computed<number>(() => {
    const seg = segments.value[currentIndex.value]
    if (!seg) return 0
    const idx = bar.value.findIndex((b) => b.barGroupKey === seg.barGroupKey)
    return idx === -1 ? 0 : idx
  })

  return { segments, bar, currentIndex, currentBarIndex }
}

function collectElectionsByType(
  types: ReadonlyArray<EnrollmentBenefitStep>,
  loader: (type: BenefitTypeEnum) => NonVpcBenefitElection[]
): Record<string, NonVpcBenefitElection[]> {
  const out: Record<string, NonVpcBenefitElection[]> = {}
  for (const step of types) {
    if (step === VPC_STEP) continue
    out[step] = loader(step as BenefitTypeEnum)
  }
  return out
}
